/**
 * Author: Andrey Morozov
 * Confidential. Proprietary code. Do not distribute without authorization.
 *
 * AddressWidget.tsx — locale-aware address search and map preview for a call.
 * It keeps the manual draft synchronized with the workspace, exposes keyboard
 * navigation for suggestions, and stores selected coordinates. Geocoding and
 * map-provider correctness belong to the injected autocomplete provider.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useWorkspace, useOperatorStore } from '@/store';
import { Panel, Button, Input } from '@/components/ui';
import { HighlightBox } from '@/features/copilot';
import {
  MockAddressProvider,
  SUGGEST_DEBOUNCE_MS,
  SUGGEST_MIN_LENGTH,
  type AddressSuggestion,
  type IAddressAutocompleteProvider,
} from './addressAutocomplete';
import type { FieldSource } from '@/store';
import { useTranslation } from '@/i18n';

const defaultProvider: IAddressAutocompleteProvider = new MockAddressProvider();

/**
 * E5-S2: Address + map widget. Plan: 14 wireframes (Screen 06).
 * Address input with autocomplete, suggestions dropdown, map preview with pin, validation warning.
 */
export function AddressWidget({
  provider = defaultProvider,
}: {
  provider?: IAddressAutocompleteProvider;
}) {
  const t = useTranslation();
  const workspace = useWorkspace();
  const setScenarioField = useOperatorStore((s) => s.setScenarioField);
  const setAddressCoords = useOperatorStore((s) => s.setAddressCoords);
  const clearAddressWarning = useOperatorStore((s) => s.clearAddressWarning);

  const addressValue = workspace.scenarioFields.address?.value ?? '';
  const addressWarning = workspace.addressWarning;
  const addressCoords = workspace.addressCoords;

  const [inputValue, setInputValue] = useState(addressValue);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync input from store when address is set externally (e.g. AI apply)
  useEffect(() => {
    setInputValue(addressValue);
  }, [addressValue]);

  const fetchSuggestions = useCallback(
    async (query: string) => {
      if (query.trim().length < SUGGEST_MIN_LENGTH) {
        setSuggestions([]);
        return;
      }
      setLoading(true);
      try {
        const list = await provider.suggest(query);
        setSuggestions(list);
        setOpen(true);
        setHighlightedIndex(0);
      } finally {
        setLoading(false);
      }
    },
    [provider]
  );

  // Debounced suggest on input change
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = inputValue.trim();
    if (q.length < SUGGEST_MIN_LENGTH) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => fetchSuggestions(inputValue), SUGGEST_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [inputValue, fetchSuggestions]);

  const handleSelect = (s: AddressSuggestion, source: FieldSource = 'manual') => {
    setScenarioField('address', s.value, source);
    setInputValue(s.value);
    setAddressCoords(s.lat != null && s.lon != null ? { lat: s.lat, lon: s.lon } : null);
    setOpen(false);
    setSuggestions([]);
    setHighlightedIndex(0);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => (i < suggestions.length - 1 ? i + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => (i > 0 ? i - 1 : suggestions.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      handleSelect(suggestions[highlightedIndex]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
      setHighlightedIndex(0);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleBlur = () => {
    // On blur, sync store with current input if user typed without selecting
    if (inputValue.trim() !== addressValue) {
      setScenarioField('address', inputValue.trim(), 'manual');
    }
    setTimeout(() => setOpen(false), 150);
  };

  // Outside click to close dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSaveAddress = () => {
    clearAddressWarning();
  };

  return (
    <HighlightBox widgetId="map_widget">
      <Panel title={t('address.title')} padding="md">
        <div className="space-y-4">
          <div className="relative" ref={dropdownRef}>
            <label
              htmlFor="address-input"
              className="block text-sm font-medium mb-1"
              style={{ color: 'var(--text-secondary)' }}
            >
              {t('address.label')}
            </label>
            <Input
              id="address-input"
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleInputKeyDown}
              onBlur={handleBlur}
              onFocus={() => suggestions.length > 0 && setOpen(true)}
              placeholder={t('address.placeholder')}
              inputClassName="text-sm"
              aria-autocomplete="list"
              aria-expanded={open}
              aria-controls="address-suggestions"
              aria-activedescendant={
                open && suggestions.length > 0 ? `address-option-${highlightedIndex}` : undefined
              }
              aria-describedby={addressWarning ? 'address-warning-message' : undefined}
            />
            {loading && (
              <span
                className="absolute right-3 top-9 text-xs"
                style={{ color: 'var(--text-muted)' }}
                aria-hidden
              >
                …
              </span>
            )}
            {open && suggestions.length > 0 && (
              <ul
                id="address-suggestions"
                role="listbox"
                className="absolute z-10 mt-1 w-full rounded-md border shadow-lg max-h-48 overflow-auto"
                style={{ borderColor: 'var(--glass-border)', background: 'var(--glass-bg-from)' }}
              >
                {suggestions.map((s, i) => (
                  <li
                    key={`${s.value}-${i}`}
                    id={`address-option-${i}`}
                    role="option"
                    aria-selected={i === highlightedIndex}
                    className={`px-3 py-2 text-sm cursor-pointer border-b last:border-b-0 ${
                      i === highlightedIndex ? 'bg-sky-100 dark:bg-sky-900/40' : ''
                    }`}
                    style={{ color: 'var(--text-primary)', borderColor: 'var(--glass-border)' }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSelect(s);
                    }}
                    onMouseEnter={() => setHighlightedIndex(i)}
                  >
                    {s.label}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {addressWarning && (
            <div
              id="address-warning-message"
              className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 text-sm text-amber-800 dark:text-amber-200"
              role="alert"
            >
              <p className="font-medium">{addressWarning.message}</p>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2"
                onClick={() => clearAddressWarning()}
              >
                {t('common.close')}
              </Button>
            </div>
          )}

          {addressCoords ? (
            <div
              className="rounded-md border overflow-hidden"
              style={{ borderColor: 'var(--glass-border)', background: 'var(--toggle-bg)' }}
            >
              <div className="aspect-video w-full relative">
                <iframe
                  title={t('address.map_preview')}
                  src={buildOsmEmbedUrl(addressCoords.lon, addressCoords.lat)}
                  className="w-full h-full border-0"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </div>
              <p className="px-2 py-1.5 text-xs" style={{ color: 'var(--text-muted)' }}>
                {addressCoords.lat.toFixed(4)}, {addressCoords.lon.toFixed(4)} ·{' '}
                <a
                  href={buildYandexMapsUrl(addressCoords.lat, addressCoords.lon)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 dark:text-blue-400 hover:underline"
                >
                  {t('address.open_yandex')}
                </a>
              </p>
            </div>
          ) : (
            <div
              className="rounded-md border border-dashed aspect-video flex items-center justify-center"
              style={{ borderColor: 'var(--glass-border)', background: 'var(--toggle-bg)' }}
            >
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                {t('address.select_for_preview')}
              </p>
            </div>
          )}

          <Button variant="secondary" size="sm" onClick={handleSaveAddress}>
            {t('address.save')}
          </Button>
        </div>
      </Panel>
    </HighlightBox>
  );
}

function buildOsmEmbedUrl(lon: number, lat: number): string {
  const delta = 0.01;
  const bbox = [lon - delta, lat - delta, lon + delta, lat + delta].join(',');
  return `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${lat}%2C${lon}`;
}

function buildYandexMapsUrl(lat: number, lon: number): string {
  return `https://yandex.ru/maps/?pt=${lon},${lat}&z=17&l=map`;
}
